import { useEffect, useSyncExternalStore } from 'react'
import { queryClient } from '@/queryClient'
import {
  clearAppPreferences,
  getSelectedFarmId,
  setSelectedFarmId,
  subscribe,
} from '@/services/appPreferences/selectedFarmStorage'
import type { User } from '@/services/farmMapping/getFarms'
import { errors } from '@/services/ruutsApi/errors'
import { useFarm } from './data/useFarm'
import { useProgram } from './data/useProgram'
import { useProgramConfig } from './data/useProgramConfig'

export const selectFarm = (farmId: string) => setSelectedFarmId(farmId)

export const clearSelectedFarm = () => {
  clearAppPreferences()
  queryClient.clear()
}

export const useSelectedFarm = (user: User | undefined) => {
  const selectedFarmId = useSyncExternalStore(subscribe, getSelectedFarmId)

  const {
    data: currentFarm,
    error: farmError,
    isError: isFarmError,
  } = useFarm(selectedFarmId ?? undefined, user)
  const { data: programConfig } = useProgramConfig(currentFarm?.id)
  const { data: program } = useProgram(programConfig?.programId)

  // Only a business error (the farm is gone, or this user may not see it) means the stored id is
  // worthless — drop it so the farm selector takes over. A 5xx or an offline blip arrives as an
  // UnexpectedEntityError and must keep the selection so a retry can still recover it.
  useEffect(() => {
    if (farmError instanceof errors.BusinessEntityError) setSelectedFarmId(null)
  }, [farmError])

  return { currentFarm, programConfig, program, isFarmError }
}
